import { useQuery, useQueryClient } from "@tanstack/react-query";
// PageHeader replaces the icon + back-link composite.
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { Badge } from "../components/Badge";
import { ErrorPanel, Loading } from "../components/Loading";
import { EditableField } from "../components/ui/EditableField";
import { PageHeader } from "../components/ui/PageHeader";
import { api } from "../lib/api";

export function LoanProductDetailPage() {
  const { t } = useTranslation();
  const { loanProductId } = useParams<{ loanProductId: string }>();
  const qc = useQueryClient();

  const lpQ = useQuery({
    queryKey: ["loan-product", loanProductId],
    queryFn: () => api.getLoanProduct(loanProductId!),
    enabled: !!loanProductId,
  });

  if (lpQ.isLoading) return <Loading />;
  if (lpQ.error) return <ErrorPanel message={(lpQ.error as Error).message} />;
  if (!lpQ.data) return <ErrorPanel message={t("policies.loan_products_not_found")} />;

  const lp = lpQ.data;

  const patch = (patch: Record<string, unknown>) =>
    api.updateLoanProduct(lp.id, patch).then((updated) => {
      qc.setQueryData(["loan-product", lp.id], updated);
      qc.invalidateQueries({ queryKey: ["loan-products"] });
      return updated;
    });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`${lp.code} · ${lp.name_en}`}
        breadcrumbs={[
          { label: t("policies.loan_products_back"), href: "/policies/loan-products" },
          { label: lp.code },
        ]}
        actions={
          !lp.is_active ? (
            <Badge tone="slate">{t("policies.loan_products_inactive_badge")}</Badge>
          ) : undefined
        }
      />

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.loan_products_section_identity")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("policies.name_en_label")}
            value={lp.name_en}
            onSave={(v) => patch({ name_en: v })}
          />
          <EditableField
            label={t("policies.name_ar_label")}
            value={lp.name_ar}
            rtl
            onSave={(v) => patch({ name_ar: v })}
          />
          <EditableField
            label={t("policies.description_label")}
            value={lp.description}
            kind="textarea"
            onSave={(v) => patch({ description: v })}
          />
          <EditableField
            label={t("policies.loan_products_currency")}
            value={lp.currency}
            onSave={(v) => patch({ currency: v })}
          />
          <EditableField
            label={t("policies.active_label")}
            value={lp.is_active}
            kind="boolean"
            onSave={(v) => patch({ is_active: v })}
          />
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.loan_products_section_limits")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("policies.loan_products_max_amount")}
            value={lp.max_amount}
            kind="number"
            onSave={(v) => patch({ max_amount: String(v) })}
          />
          <EditableField
            label={t("policies.loan_products_max_tenure")}
            value={lp.max_tenure_months}
            kind="number"
            onSave={(v) => patch({ max_tenure_months: Number(v) })}
          />
          <EditableField
            label={t("policies.loan_products_interest_rate")}
            value={lp.interest_rate}
            kind="number"
            onSave={(v) => patch({ interest_rate: String(v) })}
          />
          <EditableField
            label={t("policies.loan_products_min_service_long")}
            value={lp.min_service_months_eligibility}
            kind="number"
            onSave={(v) =>
              patch({ min_service_months_eligibility: Number(v) })
            }
          />
          <EditableField
            label={t("policies.loan_products_max_concurrent_long")}
            value={lp.max_concurrent_loans_per_employee}
            kind="number"
            onSave={(v) =>
              patch({ max_concurrent_loans_per_employee: Number(v) })
            }
          />
          <EditableField
            label={t("policies.loan_products_repayment_frequency")}
            value={lp.repayment_frequency}
            kind="select"
            options={[
              { value: "monthly", label: t("policies.loan_products_freq_monthly") },
              { value: "biweekly", label: t("policies.loan_products_freq_biweekly") },
              { value: "weekly", label: t("policies.loan_products_freq_weekly") },
            ]}
            onSave={(v) => patch({ repayment_frequency: v })}
          />
          <EditableField
            label={t("policies.loan_products_early_settlement_allowed")}
            value={lp.early_settlement_allowed}
            kind="boolean"
            onSave={(v) => patch({ early_settlement_allowed: v })}
          />
        </div>
      </section>

      {/* M3X — Sharia + Advance product classification */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.loan_products_section_sharia", "Sharia structure")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t(
              "policies.loan_products_sharia_structure_type",
              "Sharia structure type",
            )}
            value={lp.sharia_structure_type ?? "none"}
            kind="select"
            options={[
              { value: "none", label: "none" },
              { value: "murabaha", label: "murabaha" },
              { value: "qard_hasan", label: "qard_hasan" },
              { value: "takaful_equivalent", label: "takaful_equivalent" },
            ]}
            onSave={(v) => patch({ sharia_structure_type: v })}
          />
          <EditableField
            label={t(
              "policies.loan_products_is_car_loan",
              "Is car loan",
            )}
            value={lp.is_car_loan ?? false}
            kind="boolean"
            onSave={(v) => patch({ is_car_loan: v })}
          />
          <EditableField
            label={t(
              "policies.loan_products_car_loan_percentage",
              "Car loan percentage",
            )}
            value={lp.car_loan_percentage ?? null}
            kind="number"
            onSave={(v) => patch({ car_loan_percentage: String(v) })}
          />
          <EditableField
            label={t(
              "policies.loan_products_bond_max_years",
              "Bond max years",
            )}
            value={lp.bond_max_years ?? null}
            kind="number"
            onSave={(v) => patch({ bond_max_years: Number(v) })}
          />
          <EditableField
            label={t(
              "policies.loan_products_bond_pro_ration_formula",
              "Bond pro-ration formula",
            )}
            value={lp.bond_pro_ration_formula ?? ""}
            onSave={(v) => patch({ bond_pro_ration_formula: v })}
          />
        </div>
      </section>

      {/* M3X — AI automation knobs */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.loan_products_section_ai", "AI automation")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t(
              "policies.loan_products_ai_auto_approval_threshold",
              "AI auto-approval threshold (amount)",
            )}
            value={lp.ai_auto_approval_threshold_amount ?? null}
            kind="number"
            onSave={(v) =>
              patch({ ai_auto_approval_threshold_amount: String(v) })
            }
          />
          <EditableField
            label={t(
              "policies.loan_products_ai_can_propose_refinance",
              "AI can propose refinance",
            )}
            value={lp.ai_can_propose_refinance ?? false}
            kind="boolean"
            onSave={(v) => patch({ ai_can_propose_refinance: v })}
          />
          <EditableField
            label={t(
              "policies.loan_products_ai_can_propose_tenure_extension",
              "AI can propose tenure extension",
            )}
            value={lp.ai_can_propose_tenure_extension ?? false}
            kind="boolean"
            onSave={(v) => patch({ ai_can_propose_tenure_extension: v })}
          />
          <EditableField
            label={t(
              "policies.loan_products_ai_default_risk_scoring_enabled",
              "AI default-risk scoring enabled",
            )}
            value={lp.ai_default_risk_scoring_enabled ?? false}
            kind="boolean"
            onSave={(v) => patch({ ai_default_risk_scoring_enabled: v })}
          />
          <EditableField
            label={t(
              "policies.loan_products_ai_proactive_outreach_enabled",
              "AI proactive outreach enabled",
            )}
            value={lp.ai_proactive_outreach_enabled ?? false}
            kind="boolean"
            onSave={(v) => patch({ ai_proactive_outreach_enabled: v })}
          />
        </div>
      </section>

      {/* M3X — Settlement, early-settlement, calculation, eligibility */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t(
            "policies.loan_products_section_settlement",
            "Settlement & calculation",
          )}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("policies.loan_products_min_settlement_months", "Min settlement months")}
            value={lp.min_settlement_months ?? null}
            kind="number"
            onSave={(v) => patch({ min_settlement_months: Number(v) })}
          />
          <EditableField
            label={t("policies.loan_products_min_settlement_amount", "Min settlement allowable amount")}
            value={lp.min_settlement_allowable_amount ?? null}
            kind="number"
            onSave={(v) =>
              patch({ min_settlement_allowable_amount: String(v) })
            }
          />
          <EditableField
            label={t("policies.loan_products_max_settlement_amount", "Max settlement allowable amount")}
            value={lp.max_settlement_allowable_amount ?? null}
            kind="number"
            onSave={(v) =>
              patch({ max_settlement_allowable_amount: String(v) })
            }
          />
          <EditableField
            label={t("policies.loan_products_early_settlement_discount", "Early settlement discount")}
            value={lp.early_settlement_discount ?? null}
            kind="number"
            onSave={(v) => patch({ early_settlement_discount: String(v) })}
          />
          <EditableField
            label={t("policies.loan_products_early_settlement_penalty", "Early settlement penalty")}
            value={lp.early_settlement_penalty ?? null}
            kind="number"
            onSave={(v) => patch({ early_settlement_penalty: String(v) })}
          />
          <EditableField
            label={t("policies.loan_products_allow_installment_change", "Allow installment change")}
            value={lp.allow_installment_change ?? false}
            kind="boolean"
            onSave={(v) => patch({ allow_installment_change: v })}
          />
          <EditableField
            label={t("policies.loan_products_formula_multiplier", "Formula multiplier")}
            value={lp.formula_multiplier ?? null}
            kind="number"
            onSave={(v) => patch({ formula_multiplier: String(v) })}
          />
          <EditableField
            label={t("policies.loan_products_min_value", "Min value")}
            value={lp.min_value ?? null}
            kind="number"
            onSave={(v) => patch({ min_value: String(v) })}
          />
          <EditableField
            label={t("policies.loan_products_user_can_change", "User can change calculated amount")}
            value={lp.user_can_change_calculated_amount ?? false}
            kind="boolean"
            onSave={(v) => patch({ user_can_change_calculated_amount: v })}
          />
          <EditableField
            label={t("policies.loan_products_follow_formula", "Follow formula on loan amount")}
            value={lp.follow_formula_on_loan_amount ?? false}
            kind="boolean"
            onSave={(v) => patch({ follow_formula_on_loan_amount: v })}
          />
        </div>
      </section>

      {/* M3X — Eligibility extensions (probation / nationality / iqama buffer / cross-loan) */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.loan_products_section_eligibility", "Eligibility extensions")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("policies.loan_products_loan_during_probation", "Loan during probation")}
            value={lp.loan_during_probation ?? ""}
            onSave={(v) => patch({ loan_during_probation: v })}
          />
          <EditableField
            label={t("policies.loan_products_valid_for_nationalities", "Valid for nationalities (comma sep)")}
            value={lp.loan_valid_for_nationalities ?? ""}
            onSave={(v) => patch({ loan_valid_for_nationalities: v })}
          />
          <EditableField
            label={t("policies.loan_products_iqama_tenure_buffer", "Iqama tenure buffer (days)")}
            value={lp.iqama_tenure_buffer_days ?? null}
            kind="number"
            onSave={(v) => patch({ iqama_tenure_buffer_days: Number(v) })}
          />
          <EditableField
            label={t("policies.loan_products_eligible_after_months", "Eligible after months")}
            value={lp.eligible_after_months ?? null}
            kind="number"
            onSave={(v) => patch({ eligible_after_months: Number(v) })}
          />
          <EditableField
            label={t("policies.loan_products_eligible_from", "Eligible from (date YYYY-MM-DD)")}
            value={lp.eligible_from ?? ""}
            onSave={(v) => patch({ eligible_from: v })}
          />
          <EditableField
            label={t("policies.loan_products_allow_same_type", "Allow with other loans of same type")}
            value={lp.allow_with_other_loans_same_type ?? false}
            kind="boolean"
            onSave={(v) => patch({ allow_with_other_loans_same_type: v })}
          />
          <EditableField
            label={t("policies.loan_products_allow_any_type", "Allow with other loans of any type")}
            value={lp.allow_with_other_loans_any_type ?? false}
            kind="boolean"
            onSave={(v) => patch({ allow_with_other_loans_any_type: v })}
          />
          <EditableField
            label={t("policies.loan_products_is_paid_with_leave", "Is paid with leave")}
            value={lp.is_paid_with_leave ?? false}
            kind="boolean"
            onSave={(v) => patch({ is_paid_with_leave: v })}
          />
          <EditableField
            label={t("policies.loan_products_repayment_during_unpaid_leave", "Repayment during unpaid leave")}
            value={lp.repayment_during_unpaid_leave ?? ""}
            onSave={(v) => patch({ repayment_during_unpaid_leave: v })}
          />
          <EditableField
            label={t("policies.loan_products_repayment_during_reduced_pay_leave", "Repayment during reduced-pay leave")}
            value={lp.repayment_during_reduced_pay_leave ?? ""}
            onSave={(v) => patch({ repayment_during_reduced_pay_leave: v })}
          />
        </div>
      </section>

      {/* M3X — Guarantor + EOS cross-check + external routing */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.loan_products_section_guarantor", "Guarantor & EOS cross-check")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("policies.loan_products_guarantor_required_for_all", "Guarantor required for all")}
            value={lp.guarantor_required_for_all ?? false}
            kind="boolean"
            onSave={(v) => patch({ guarantor_required_for_all: v })}
          />
          <EditableField
            label={t("policies.loan_products_guarantor_required_above_amount", "Guarantor required above amount")}
            value={lp.guarantor_required_above_amount ?? null}
            kind="number"
            onSave={(v) =>
              patch({ guarantor_required_above_amount: String(v) })
            }
          />
          <EditableField
            label={t("policies.loan_products_guarantor_min_grade", "Guarantor min grade")}
            value={lp.guarantor_min_grade ?? ""}
            onSave={(v) => patch({ guarantor_min_grade: v })}
          />
          <EditableField
            label={t("policies.loan_products_eos_compensation_percentage", "EOS compensation percentage")}
            value={lp.eos_compensation_percentage ?? null}
            kind="number"
            onSave={(v) => patch({ eos_compensation_percentage: String(v) })}
          />
          <EditableField
            label={t("policies.loan_products_value_should_not_exceed_eos", "Value should not exceed EOS compensation")}
            value={lp.value_should_not_exceed_eos_compensation ?? false}
            kind="boolean"
            onSave={(v) =>
              patch({ value_should_not_exceed_eos_compensation: v })
            }
          />
          <EditableField
            label={t("policies.loan_products_validate_loan_amount_vs_eos", "Validate loan amount against EOS compensation")}
            value={lp.validate_loan_amount_against_eos_compensation ?? false}
            kind="boolean"
            onSave={(v) =>
              patch({
                validate_loan_amount_against_eos_compensation: v,
              })
            }
          />
          <EditableField
            label={t("policies.loan_products_external_beneficiary_routing", "External beneficiary routing")}
            value={lp.external_beneficiary_routing ?? ""}
            onSave={(v) => patch({ external_beneficiary_routing: v })}
          />
        </div>
      </section>
    </div>
  );
}
